import { useCallback, useMemo, useReducer } from 'react';
import type { Reducer } from 'react';

import type { Id } from '@e-socna/data-layer';

import { generateId } from '../../../utils/generate-id.util';

import { UiContextActionType } from '../enums/action-type.enum';
import { serializeBackgroundOperationKey } from '../utils/serialize-background-operation-key.util';
import { initialState } from '../types/state.type';
import type { BackgroundOperationType } from '../enums/background-operation-type.enum';
import type { BackgroundOperationKey } from '../types/background-operation-key.type';
import type { UiContextAction } from '../types/actions.type';
import type { UiState } from '../types/state.type';

export const useUiReducer = () => {
  const uiReducer: Reducer<UiState, UiContextAction> = (
    state: UiState,
    action: UiContextAction,
  ) => {
    const actionType = action.type;

    switch (actionType) {
      case UiContextActionType.EnableTextEditorFullScreenMode:
        return {
          ...state,
          isTextEditorFullscreen: action.payload,
        };
      case UiContextActionType.EnableImageEditorFullScreenMode:
        return {
          ...state,
          isImageEditorFullscreen: action.payload,
        };
      case UiContextActionType.StartBackgroundOperation: {
        const serializedKey = serializeBackgroundOperationKey(action.payload);

        if (state.backgroundOperationKeys.includes(serializedKey)) {
          return state;
        }

        return {
          ...state,
          backgroundOperationKeys: [
            ...state.backgroundOperationKeys,
            serializedKey,
          ],
          isBackgroundOperationRunning: true,
        };
      }
      case UiContextActionType.EndBackgroundOperation: {
        const serializedKey = serializeBackgroundOperationKey(action.payload);
        const updatedKeys = state.backgroundOperationKeys.filter(
          (key) => key !== serializedKey,
        );

        return {
          ...state,
          backgroundOperationKeys: updatedKeys,
          isBackgroundOperationRunning: !!updatedKeys.length,
        };
      }
      case UiContextActionType.EndBackgroundOperationsByType: {
        const typePrefix = `${action.payload}:`;
        const updatedKeys = state.backgroundOperationKeys.filter(
          (key) => key !== action.payload && !key.startsWith(typePrefix),
        );

        return {
          ...state,
          backgroundOperationKeys: updatedKeys,
          isBackgroundOperationRunning: !!updatedKeys.length,
        };
      }
      case UiContextActionType.ShowDataGrid:
        return {
          ...state,
          visibleDataGridTokens: [
            ...state.visibleDataGridTokens,
            action.payload,
          ],
          isDataGridVisible: true,
        };
      case UiContextActionType.HideDataGrid: {
        const newListOfTokens = state.visibleDataGridTokens.filter(
          (token) => token !== action.payload,
        );

        return {
          ...state,
          visibleDataGridTokens: newListOfTokens,
          isDataGridVisible: !!newListOfTokens.length,
        };
      }
      default:
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `Unknown action type in UiContext reducer: ${actionType}`,
        );
    }
  };

  const [state, dispatch] = useReducer(uiReducer, initialState);

  const enableTextEditorFullScreenMode = useCallback((isEnabled: boolean) => {
    dispatch({
      type: UiContextActionType.EnableTextEditorFullScreenMode,
      payload: isEnabled,
    });
  }, []);

  const enableImageEditorFullScreenMode = useCallback((isEnabled: boolean) => {
    dispatch({
      type: UiContextActionType.EnableImageEditorFullScreenMode,
      payload: isEnabled,
    });
  }, []);

  const startBackgroundOperation = useCallback(
    (key: Readonly<BackgroundOperationKey>) => {
      dispatch({
        type: UiContextActionType.StartBackgroundOperation,
        payload: key,
      });
    },
    [],
  );

  const endBackgroundOperation = useCallback(
    (key: Readonly<BackgroundOperationKey>) => {
      dispatch({
        type: UiContextActionType.EndBackgroundOperation,
        payload: key,
      });
    },
    [],
  );

  const endBackgroundOperationsByType = useCallback(
    (operationType: BackgroundOperationType) => {
      dispatch({
        type: UiContextActionType.EndBackgroundOperationsByType,
        payload: operationType,
      });
    },
    [],
  );

  const showDataGrid = useCallback(() => {
    const token = generateId();

    dispatch({
      type: UiContextActionType.ShowDataGrid,
      payload: token,
    });

    return token;
  }, []);

  const hideDataGrid = useCallback((token: Id) => {
    dispatch({
      type: UiContextActionType.HideDataGrid,
      payload: token,
    });
  }, []);

  return useMemo(
    () => ({
      ui: state,
      dispatchUi: dispatch,
      enableTextEditorFullScreenMode,
      enableImageEditorFullScreenMode,
      startBackgroundOperation,
      endBackgroundOperation,
      endBackgroundOperationsByType,
      showDataGrid,
      hideDataGrid,
    }),
    [
      state,
      dispatch,
      enableTextEditorFullScreenMode,
      enableImageEditorFullScreenMode,
      startBackgroundOperation,
      endBackgroundOperation,
      endBackgroundOperationsByType,
      showDataGrid,
      hideDataGrid,
    ],
  );
};
